// Dashboard Poids — stats, courbe (zone saine, paliers), insights, historique éditable.
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { type FormEvent, useMemo, useState } from 'react';
import { WeightChart } from '@/components/charts';
import { InsightsCard } from '@/components/InsightsCard';
import {
  ChartCard,
  EmptyState,
  ErrorBanner,
  Kpi,
  PeriodPicker,
  PhaseBadge,
  ProgressBar,
  Segmented,
} from '@/components/ui';
import { WeighInReminder } from '@/components/WeighInReminder';
import {
  adherence,
  aggregateBy,
  BMI_LABELS,
  bmi,
  bmiCategory,
  buildInsights,
  consumptionNutrients,
  currentWeight,
  filterByPeriod,
  healthyWeightRange,
  isPlateau,
  mean,
  milestoneProgress,
  type Period,
  progress,
  projectReachDate,
  round,
  type SmoothingMethod,
  smoothedWeight,
  sumNutrients,
  todayKey,
  volatility,
  weightDistribution,
  weightTrend,
} from '@/domain';
import {
  useConsumptions,
  useCreateMilestone,
  useCreateWeight,
  useDeleteMilestone,
  useDeleteWeight,
  useMilestones,
  useProfile,
  useUpdateWeight,
  useWeights,
} from '@/hooks/queries';
import { useSubmit } from '@/hooks/useSubmit';
import { confirmDelete } from '@/lib/confirm';
import { useTheme } from '@/theme/ThemeContext';

export function WeightPage() {
  const { data: profile } = useProfile();
  const { data: weights = [] } = useWeights();
  const { data: milestones = [] } = useMilestones();
  const { data: consumptions = [] } = useConsumptions();
  const createWeight = useCreateWeight();
  const updateWeight = useUpdateWeight();
  const deleteWeight = useDeleteWeight();
  const createMilestone = useCreateMilestone();
  const deleteMilestone = useDeleteMilestone();
  const weightSubmit = useSubmit();
  const milestoneSubmit = useSubmit();
  const { palette } = useTheme();

  const [date, setDate] = useState(todayKey());
  const [kg, setKg] = useState('');
  const [period, setPeriod] = useState<Period>('all');
  const [smoothing, setSmoothing] = useState<SmoothingMethod>('sma');
  const [msWeight, setMsWeight] = useState('');
  const [msLabel, setMsLabel] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editKg, setEditKg] = useState('');

  const stats = useMemo(() => {
    if (weights.length === 0 || !profile) return null;
    const p = profile.profile;
    const prog = progress(weights, p.targetWeightKg, p.idealWeightKg);
    const trend = weightTrend(weights);
    const eta = projectReachDate(weights, p.targetWeightKg);
    const vol = volatility(weights);
    const mp = milestoneProgress(weights, milestones);
    const recent = weights.slice(-8);
    const weekDelta =
      recent.length >= 2 ? round(currentWeight(weights) - recent[0]!.weightKg, 2) : 0;
    const bmiValue = bmi(prog.currentKg, p.heightCm);
    return { prog, trend, eta, vol, mp, plateau: isPlateau(weights), weekDelta, bmiValue };
  }, [weights, milestones, profile]);

  const chartData = useMemo(
    () =>
      smoothedWeight(
        filterByPeriod(weights, (w) => w.date, period),
        7,
        smoothing,
      ),
    [weights, period, smoothing],
  );

  const distribution = useMemo(
    () => weightDistribution(filterByPeriod(weights, (w) => w.date, period)),
    [weights, period],
  );

  const insights = useMemo(() => {
    if (!stats || !profile) return [];
    const p = profile.profile;
    const adh = adherence(consumptions, p.calorieThreshold, p.trainingPhase);
    const dailyProtein = aggregateBy(
      consumptions,
      (c) => c.datetime,
      'day',
      (g) => sumNutrients(g.map(consumptionNutrients)).protein,
    ).map((b) => b.value);
    return buildInsights({
      trendPerWeek: stats.trend.slopePerWeek,
      trainingPhase: p.trainingPhase,
      targetProgressPct: stats.prog.targetProgressPct,
      toTargetKg: stats.prog.toTargetKg,
      plateau: stats.plateau,
      etaDays: stats.eta?.days ?? null,
      adherencePct: adh.adherencePct,
      currentStreak: adh.currentStreak,
      proteinAvgG: dailyProtein.length ? mean(dailyProtein) : 0,
      proteinTargetG: p.proteinTargetG,
    });
  }, [stats, profile, consumptions]);

  function addWeight(e: FormEvent) {
    e.preventDefault();
    const value = Number(kg);
    if (!kg || !Number.isFinite(value)) return;
    void weightSubmit.run(async () => {
      await createWeight.mutateAsync({ date, weight_kg: value });
      setKg('');
    });
  }

  function addMilestone(e: FormEvent) {
    e.preventDefault();
    const target = Number(msWeight);
    if (!msWeight || !msLabel || !Number.isFinite(target)) return;
    void milestoneSubmit.run(async () => {
      await createMilestone.mutateAsync({ target_weight_kg: target, label: msLabel });
      setMsWeight('');
      setMsLabel('');
    });
  }

  async function saveEdit(id: string) {
    const w = weights.find((x) => x.id === id);
    const value = Number(editKg);
    if (w && editKg && Number.isFinite(value)) {
      await weightSubmit.run(() =>
        updateWeight.mutateAsync({ id, date: w.date, weight_kg: value }).then(() => undefined),
      );
    }
    setEditId(null);
  }

  // Phase : oriente le sens « favorable » des indicateurs de poids (perte en sèche,
  // prise en masse, stabilité en récup → aucune direction « bonne », delta neutre).
  const phase = profile?.profile.trainingPhase ?? 'cut';
  const gaining = phase === 'bulk';
  const recovering = phase === 'recovery';

  return (
    <>
      <div className="page-header">
        <div>
          <h1>
            Poids <PhaseBadge phase={phase} />
          </h1>
          <p>Suivi de la courbe, de la tendance et des paliers.</p>
        </div>
        <form className="toolbar" onSubmit={addWeight} style={{ margin: 0 }}>
          <div>
            <label htmlFor="wdate">Date</label>
            <input id="wdate" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label htmlFor="wkg">Poids (kg)</label>
            <input
              id="wkg"
              type="number"
              step="0.1"
              min={20}
              max={400}
              value={kg}
              onChange={(e) => setKg(e.target.value)}
              placeholder="ex. 79.4"
              style={{ maxWidth: 130 }}
            />
          </div>
          <button type="submit" disabled={weightSubmit.pending}>
            Ajouter
          </button>
        </form>
      </div>

      {weightSubmit.error && (
        <ErrorBanner message={weightSubmit.error} onRetry={weightSubmit.reset} />
      )}

      <WeighInReminder weights={weights} />

      {!stats ? (
        <EmptyState>Ajoute ta première pesée pour démarrer le suivi.</EmptyState>
      ) : (
        <>
          <div className="grid kpis" style={{ marginBottom: '1rem' }}>
            <Kpi
              label="Poids actuel"
              value={stats.prog.currentKg}
              unit="kg"
              delta={{
                value: stats.weekDelta,
                unit: ' kg/7j',
                goodWhenNegative: !gaining,
                neutral: recovering,
              }}
            />
            {recovering ? (
              <Kpi
                label="Variation depuis le début"
                value={round(-stats.prog.lostKg, 2)}
                unit="kg"
              />
            ) : gaining ? (
              <Kpi label="Pris depuis le début" value={round(-stats.prog.lostKg, 2)} unit="kg" />
            ) : (
              <Kpi label="Perdu depuis le début" value={stats.prog.lostKg} unit="kg" />
            )}
            <Kpi
              label="Tendance"
              value={round(stats.trend.slopePerWeek, 2)}
              unit="kg/sem"
              hint={`R² = ${round(stats.trend.r2, 2)} · ${stats.plateau ? '⏸ plateau' : '📉 en cours'}`}
            />
            <Kpi label="Reste (cible)" value={stats.prog.toTargetKg} unit="kg" />
            <Kpi
              label="Objectif atteint dans"
              value={stats.eta ? `${stats.eta.days}` : '—'}
              unit={stats.eta ? 'j' : ''}
              hint={
                stats.eta
                  ? `≈ ${format(parseISO(stats.eta.date), 'd MMM yyyy', { locale: fr })}`
                  : 'tendance actuelle n’y mène pas'
              }
            />
            <Kpi
              label="Volatilité (bruit/j)"
              value={stats.vol.dailyNoiseKg}
              unit="kg"
              hint={`amplitude ${stats.vol.rangeKg} kg`}
            />
            <Kpi
              label="IMC"
              value={stats.bmiValue}
              hint={`${BMI_LABELS[bmiCategory(stats.bmiValue)]} · ne distingue pas masse grasse/musculaire`}
            />
          </div>

          {insights.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <InsightsCard insights={insights} />
            </div>
          )}

          <div className="grid charts">
            <ChartCard
              title="Évolution du poids"
              subtitle={`brut + lissage ${smoothing === 'sma' ? 'MM7j' : 'EWMA'} · bandeau vert = zone de poids saine (IMC 18.5–24.9)`}
              actions={
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Segmented
                    label="Lissage"
                    value={smoothing}
                    options={[
                      { key: 'sma', label: 'MM7j' },
                      { key: 'ewma', label: 'EWMA' },
                    ]}
                    onChange={setSmoothing}
                  />
                  <PeriodPicker value={period} onChange={setPeriod} />
                </div>
              }
            >
              <WeightChart
                data={chartData}
                targetKg={profile!.profile.targetWeightKg}
                idealKg={profile!.profile.idealWeightKg}
                palette={palette}
                healthyRange={healthyWeightRange(profile!.profile.heightCm)}
                milestones={milestones}
              />
            </ChartCard>

            <div className="card">
              <h3>Progression</h3>
              <div style={{ marginTop: '0.75rem' }}>
                <ProgressLine
                  label="Vers la cible"
                  pct={stats.prog.targetProgressPct}
                  color={palette.yellow}
                />
                <ProgressLine
                  label="Vers l'idéal"
                  pct={stats.prog.idealProgressPct}
                  color={palette.good}
                />
              </div>

              <h3 style={{ marginTop: '1.25rem' }}>Paliers</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0.75rem' }}>
                {stats.mp.map((m) => (
                  <li
                    key={m.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.3rem 0',
                    }}
                  >
                    <span>
                      {m.reached ? '✅' : '⬜'} {m.label} ({m.targetWeightKg} kg)
                    </span>
                    <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span className="muted">{m.reached ? 'atteint' : `${m.remainingKg} kg`}</span>
                      <button
                        type="button"
                        className="danger"
                        aria-label="Supprimer le palier"
                        onClick={() => {
                          if (confirmDelete('ce palier')) deleteMilestone.mutate(m.id);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <form className="toolbar" onSubmit={addMilestone} style={{ margin: 0 }}>
                <input
                  type="number"
                  step="0.1"
                  min={20}
                  max={400}
                  value={msWeight}
                  onChange={(e) => setMsWeight(e.target.value)}
                  placeholder="kg"
                  aria-label="Poids du palier (kg)"
                  style={{ maxWidth: 90 }}
                />
                <input
                  value={msLabel}
                  onChange={(e) => setMsLabel(e.target.value)}
                  placeholder="Libellé (ex. Étape 1)"
                  aria-label="Libellé du palier"
                  style={{ flex: 1 }}
                />
                <button type="submit" disabled={milestoneSubmit.pending}>
                  + Palier
                </button>
              </form>
              {milestoneSubmit.error && (
                <ErrorBanner message={milestoneSubmit.error} onRetry={milestoneSubmit.reset} />
              )}
            </div>
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <h3>Distribution du poids (période affichée)</h3>
            <div className="grid kpis" style={{ gap: '0.75rem', marginTop: '0.75rem' }}>
              <Kpi label="P25" value={distribution.p25} unit="kg" />
              <Kpi label="Médiane" value={distribution.median} unit="kg" />
              <Kpi label="P75" value={distribution.p75} unit="kg" />
              <Kpi
                label="IQR"
                value={distribution.iqr}
                unit="kg"
                hint="écart interquartile (dispersion robuste)"
              />
              <Kpi
                label="Coeff. de variation"
                value={round(distribution.cv * 100, 2)}
                unit="%"
                hint="dispersion relative (plus bas = plus régulier)"
              />
            </div>
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <h3>Historique des pesées</h3>
            <div
              className="table-wrap"
              style={{ maxHeight: 320, overflowY: 'auto', marginTop: '0.5rem' }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Poids</th>
                    <th>Δ</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...weights].reverse().map((w, i, arr) => {
                    const prev = arr[i + 1];
                    const delta = prev ? round(w.weightKg - prev.weightKg, 1) : null;
                    const editing = editId === w.id;
                    return (
                      <tr key={w.id}>
                        <td>{format(parseISO(w.date), 'd MMM yyyy', { locale: fr })}</td>
                        <td>
                          {editing ? (
                            <input
                              type="number"
                              step="0.1"
                              value={editKg}
                              onChange={(e) => setEditKg(e.target.value)}
                              style={{ maxWidth: 90 }}
                            />
                          ) : (
                            `${w.weightKg.toFixed(1)} kg`
                          )}
                        </td>
                        <td className={delta == null ? '' : delta <= 0 ? 'delta down' : 'delta up'}>
                          {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta}`}
                        </td>
                        <td style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
                          {editing ? (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEdit(w.id)}
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem' }}
                              >
                                OK
                              </button>
                              <button
                                type="button"
                                className="ghost"
                                aria-label="Annuler la modification"
                                onClick={() => setEditId(null)}
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem' }}
                              >
                                ×
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="ghost"
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem' }}
                                onClick={() => {
                                  setEditId(w.id);
                                  setEditKg(String(w.weightKg));
                                }}
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => {
                                  if (confirmDelete('cette pesée')) deleteWeight.mutate(w.id);
                                }}
                              >
                                Suppr.
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function ProgressLine({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</span>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {pct}%
        </span>
      </div>
      <ProgressBar pct={pct} color={color} />
    </div>
  );
}
